import { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles, Youtube, Upload, BookOpen, Trash2, ChevronDown, ChevronRight,
  Lightbulb, AlertTriangle, TrendingUp, Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

export default function AiAssistant() {
  return (
    <div className="p-6 max-w-[1240px] mx-auto space-y-6" data-testid="ai-page">
      <Header />
      <VideoSection />
      <StrategiesList />
      <JournalSection />
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-11 h-11 rounded-xl grid place-items-center"
        style={{
          background: "linear-gradient(160deg,#ff7a3c,#ff5d15)",
          boxShadow: "0 0 18px rgba(255,93,21,.4), inset 0 1px 0 rgba(255,255,255,.35)",
        }}
      >
        <Brain className="w-6 h-6 text-black" strokeWidth={2.2} />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-zinc-500">
          Gemini 2.5 Pro
        </div>
        <h1 className="text-2xl font-bold tracking-tight">AI Asistan</h1>
      </div>
    </div>
  );
}

/* ============== VIDEO → STRATEGY ============== */
function VideoSection() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("url");
  const [url, setUrl] = useState("");
  const [hint, setHint] = useState("");
  const fileInputRef = useRef(null);

  const urlMut = useMutation({
    mutationFn: () => api.aiAnalyzeUrl(url.trim(), hint.trim()),
    onSuccess: (data) => {
      toast.success(`Strateji çıkarıldı: ${data.strategy?.title || "—"}`);
      setUrl(""); setHint("");
      qc.invalidateQueries({ queryKey: ["ai-strategies"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const uploadMut = useMutation({
    mutationFn: (file) => api.aiAnalyzeUpload(file),
    onSuccess: (data) => {
      toast.success(`Strateji çıkarıldı: ${data.strategy?.title || "—"}`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["ai-strategies"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const busy = urlMut.isPending || uploadMut.isPending;

  return (
    <section className="border border-[#27272A] rounded-xl bg-[#121215]" data-testid="ai-video-section">
      <header className="px-5 py-4 border-b border-[#1A1A1A] flex items-center gap-2.5">
        <Sparkles className="w-4 h-4 text-[#ff5d15]" />
        <h2 className="text-sm font-semibold">Video → Strateji Çıkar</h2>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-2">
          Gemini videoyu izler, kuralları JSON&apos;a döker
        </span>
      </header>

      <div className="px-5 py-4">
        <div className="flex gap-2 mb-4">
          <TabBtn active={tab === "url"} onClick={() => setTab("url")} icon={<Youtube className="w-3.5 h-3.5" />}>
            Video URL
          </TabBtn>
          <TabBtn active={tab === "upload"} onClick={() => setTab("upload")} icon={<Upload className="w-3.5 h-3.5" />}>
            Dosya Yükle
          </TabBtn>
        </div>

        {tab === "url" ? (
          <div className="space-y-3">
            <Input
              data-testid="ai-video-url"
              placeholder="https://youtu.be/... veya direkt mp4 linki"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-11 bg-[#0A0A0C] border-[#27272A] font-mono text-sm"
              disabled={busy}
            />
            <Textarea
              data-testid="ai-video-hint"
              placeholder="Opsiyonel: 'ICT konseptleri', '5dk skalp', vs. — Gemini'ye ipucu"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              rows={2}
              className="bg-[#0A0A0C] border-[#27272A] text-sm"
              disabled={busy}
            />
            <Button
              data-testid="ai-analyze-url-btn"
              onClick={() => urlMut.mutate()}
              disabled={!url.trim() || busy}
              className="h-11 px-6 bg-gradient-to-b from-[#FF7034] to-[#FF5D15] text-black hover:brightness-110 font-semibold"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {urlMut.isPending ? "Gemini izliyor… (60-120sn)" : "Analiz Et"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block border-2 border-dashed border-[#27272A] rounded-lg p-8 text-center cursor-pointer hover:border-[#ff5d15]/40 transition-colors"
                   data-testid="ai-upload-zone">
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadMut.mutate(f);
                }}
              />
              <Upload className="w-7 h-7 mx-auto mb-2 text-zinc-500" />
              <div className="text-sm text-zinc-300 mb-1">
                {uploadMut.isPending ? "Yükleniyor + Gemini izliyor…" : "Video dosyası seç veya buraya bırak"}
              </div>
              <div className="text-xs text-zinc-500 font-mono">
                mp4 · mov · webm · mkv · max 100MB
              </div>
            </label>
          </div>
        )}

        {busy && (
          <div className="mt-4 flex items-center gap-2 text-xs text-zinc-400">
            <div className="w-3 h-3 rounded-full bg-[#ff5d15] animate-pulse" />
            Gemini 2.5 Pro videoyu izliyor, strateji kurallarını çıkarıyor…
          </div>
        )}
      </div>
    </section>
  );
}

function TabBtn({ active, onClick, icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`h-8 px-3 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors
        ${active
          ? "bg-[#ff5d15]/15 text-[#ff5d15] border border-[#ff5d15]/30"
          : "border border-[#27272A] text-zinc-400 hover:bg-[#1A1A1A]"}`}
    >
      {icon}{children}
    </button>
  );
}

/* ============== SAVED STRATEGIES ============== */
function StrategiesList() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["ai-strategies"],
    queryFn: api.aiListStrategies,
  });
  const [expandedId, setExpandedId] = useState(null);

  const delMut = useMutation({
    mutationFn: api.aiDeleteStrategy,
    onSuccess: () => {
      toast.success("Strateji silindi");
      qc.invalidateQueries({ queryKey: ["ai-strategies"] });
    },
  });

  const activateMut = useMutation({
    mutationFn: ({ id, active }) => api.aiActivateStrategy(id, active),
    onSuccess: (doc) => {
      toast.success(doc.is_active
        ? "Strateji aktif · sinyal motoru bunu kullanacak"
        : "Strateji pasif");
      qc.invalidateQueries({ queryKey: ["ai-strategies"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <section className="border border-[#27272A] rounded-xl bg-[#121215]">
      <header className="px-5 py-4 border-b border-[#1A1A1A] flex items-center gap-2.5">
        <BookOpen className="w-4 h-4 text-[#ff5d15]" />
        <h2 className="text-sm font-semibold">Kayıtlı Stratejiler</h2>
        <span className="text-xs text-zinc-500 ml-1">· {items.length}</span>
      </header>
      <div className="p-4 space-y-2">
        {isLoading && <div className="text-xs text-zinc-500">Yükleniyor…</div>}
        {!isLoading && items.length === 0 && (
          <div className="text-xs text-zinc-500 text-center py-6">
            Henüz strateji çıkarılmadı. Bir video gönder, Gemini analiz etsin.
          </div>
        )}
        {items.map((item) => {
          const s = item.strategy || {};
          const isOpen = expandedId === item.id;
          return (
            <div key={item.id} data-testid={`strategy-${item.id}`}
                 className="border border-[#27272A] rounded-md bg-[#0A0A0C]">
              <button
                onClick={() => setExpandedId(isOpen ? null : item.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#141418]"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">
                    {s.title || "İsimsiz strateji"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded-sm bg-[#ff5d15]/10 text-[#ff5d15] text-[10px] font-mono">
                      {s.approach || "—"}
                    </span>
                    {Array.isArray(s.timeframes) && s.timeframes.length > 0 && (
                      <span className="font-mono text-[10px]">{s.timeframes.join(" · ")}</span>
                    )}
                    {s.confidence != null && (
                      <span className="font-mono text-[10px]">güven {Math.round(s.confidence * 100)}%</span>
                    )}
                  </div>
                </div>
                <Trash2
                  className="w-4 h-4 text-zinc-600 hover:text-[#ff5662] cursor-pointer"
                  data-testid={`del-strategy-${item.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm("Stratejiyi sil?")) delMut.mutate(item.id);
                  }}
                />
              </button>
              <div
                className={`flex items-center justify-between gap-2 px-3 py-2 border-t border-[#1A1A1A] text-[11px] ${
                  item.is_active ? "bg-[#ff5d15]/8" : ""
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-zinc-500 font-mono">
                  Sinyal motoruna bağlı:{" "}
                  <b className={item.is_active ? "text-[#2bd98a]" : "text-zinc-400"}>
                    {item.is_active ? "AKTİF" : "Pasif"}
                  </b>
                  {item.is_active && (
                    <span className="text-zinc-500"> · +{item.confidence_boost ?? 15} güven boost</span>
                  )}
                </span>
                <button
                  type="button"
                  data-testid={`activate-strategy-${item.id}`}
                  className={`h-7 px-3 rounded-md text-[11px] font-semibold tracking-wider transition ${
                    item.is_active
                      ? "bg-[#ff5662]/10 text-[#ff5662] border border-[#ff5662]/30 hover:bg-[#ff5662]/20"
                      : "bg-[#2bd98a]/10 text-[#2bd98a] border border-[#2bd98a]/30 hover:bg-[#2bd98a]/20"
                  }`}
                  onClick={() => activateMut.mutate({ id: item.id, active: !item.is_active })}
                  disabled={activateMut.isPending}
                >
                  {item.is_active ? "PASİFLEŞTİR" : "AKTİFLEŞTİR"}
                </button>
              </div>
              {isOpen && <StrategyDetail strategy={s} sourceUrl={item.source_url} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StrategyDetail({ strategy: s, sourceUrl }) {
  return (
    <div className="px-4 py-3 border-t border-[#1A1A1A] space-y-3 text-xs">
      {s.summary && (
        <p className="text-zinc-300 leading-relaxed">{s.summary}</p>
      )}
      <Two label="Giriş Kuralları" items={s.entry_rules} />
      <Two label="Çıkış Kuralları" items={s.exit_rules} />
      {s.risk_management && (
        <div className="grid grid-cols-3 gap-2 bg-[#0A0A0C] p-2 rounded">
          <Pill k="Stop" v={s.risk_management.stop_pct ? `${s.risk_management.stop_pct}%` : "—"} />
          <Pill k="R/R" v={s.risk_management.rr_ratio ?? "—"} />
          <Pill k="Boyut" v={s.risk_management.position_sizing || "—"} />
        </div>
      )}
      {(s.indicators?.length || s.key_concepts?.length) && (
        <div className="flex flex-wrap gap-1.5">
          {[...(s.indicators || []), ...(s.key_concepts || [])].map((t, i) => (
            <span key={i} className="px-2 py-0.5 rounded-sm bg-[#1A1A1A] border border-[#27272A] text-[10px] text-zinc-300">
              {t}
            </span>
          ))}
        </div>
      )}
      {sourceUrl && (
        <a href={sourceUrl} target="_blank" rel="noreferrer"
           className="text-[10px] font-mono text-[#ff5d15] hover:underline">
          Kaynak ↗
        </a>
      )}
    </div>
  );
}

function Two({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-mono">{label}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-zinc-300 flex gap-2">
            <span className="text-[#ff5d15]">·</span><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pill({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{k}</div>
      <div className="text-sm text-zinc-100 mt-0.5">{v}</div>
    </div>
  );
}

/* ============== JOURNAL AI ============== */
function JournalSection() {
  const qc = useQueryClient();
  const [days, setDays] = useState(14);

  const { data: latest } = useQuery({
    queryKey: ["ai-journal-latest"],
    queryFn: api.aiJournalLatest,
  });

  const mut = useMutation({
    mutationFn: () => api.aiJournalReport(days),
    onSuccess: () => {
      toast.success("Rapor hazır");
      qc.invalidateQueries({ queryKey: ["ai-journal-latest"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const r = latest?.report;

  return (
    <section className="border border-[#27272A] rounded-xl bg-[#121215]" data-testid="ai-journal-section">
      <header className="px-5 py-4 border-b border-[#1A1A1A] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <TrendingUp className="w-4 h-4 text-[#ff5d15]" />
          <h2 className="text-sm font-semibold">Trade Journal AI</h2>
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 ml-1">
            Kendi pozisyon verinden öğrenir
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="ai-journal-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-8 px-2 bg-[#0A0A0C] border border-[#27272A] rounded-sm text-xs"
          >
            <option value={7}>7 gün</option>
            <option value={14}>14 gün</option>
            <option value={30}>30 gün</option>
            <option value={60}>60 gün</option>
          </select>
          <Button
            data-testid="ai-journal-run"
            size="sm"
            disabled={mut.isPending}
            onClick={() => mut.mutate()}
            className="h-8 px-3 bg-[#ff5d15] hover:bg-[#f0b90b] text-black rounded-sm text-xs font-semibold"
          >
            {mut.isPending ? "Analiz ediliyor…" : "Rapor Üret"}
          </Button>
        </div>
      </header>

      <div className="p-5">
        {!r && (
          <div className="text-xs text-zinc-500 text-center py-6">
            Henüz rapor yok. &quot;Rapor Üret&quot; bas.
          </div>
        )}
        {r && r.empty && (
          <div className="text-xs text-zinc-500 text-center py-6">{r.message}</div>
        )}
        {r && !r.empty && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Stat label="Kazanma Oranı" value={`${Math.round((r.win_rate || 0) * 100)}%`} />
            <Stat label="Toplam K/Z" value={`$${(r.total_pnl || 0).toFixed(2)}`}
                  positive={(r.total_pnl || 0) >= 0} />
            <Insight icon={<Lightbulb className="w-3.5 h-3.5 text-[#2bd98a]" />}
                     title="En İyi Pattern" body={r.best_pattern} />
            <Insight icon={<AlertTriangle className="w-3.5 h-3.5 text-[#ff5662]" />}
                     title="En Kötü Pattern" body={r.worst_pattern} />
            {r.insights?.length > 0 && (
              <Block title="İçgörüler" items={r.insights} />
            )}
            {r.recommendations?.length > 0 && (
              <Block title="Öneriler" items={r.recommendations} accent />
            )}
            {r.warning && (
              <div className="md:col-span-2 bg-[#1A0F00] border-l-4 border-[#ff5d15] px-3 py-2.5 rounded-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#ff5d15] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-zinc-200 leading-relaxed">{r.warning}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, positive }) {
  return (
    <div className="bg-[#0A0A0C] border border-[#27272A] rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
      <div className={`text-2xl font-mono font-semibold mt-1 ${
        positive === true ? "text-[#2bd98a]" : positive === false ? "text-[#ff5662]" : "text-zinc-100"
      }`}>{value}</div>
    </div>
  );
}

function Insight({ icon, title, body }) {
  return (
    <div className="bg-[#0A0A0C] border border-[#27272A] rounded-md px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
        {icon}{title}
      </div>
      <p className="text-xs text-zinc-200 mt-1.5 leading-relaxed">{body}</p>
    </div>
  );
}

function Block({ title, items, accent }) {
  return (
    <div className={`md:col-span-2 bg-[#0A0A0C] border ${accent ? "border-[#ff5d15]/25" : "border-[#27272A]"} rounded-md px-4 py-3`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">{title}</div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-zinc-200 flex gap-2 leading-relaxed">
            <span className={accent ? "text-[#ff5d15]" : "text-zinc-500"}>›</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
